import type { FeatureKey, Presence } from "./types";
import { formatBlackspaceDateTime } from "../../utils/time";

export function formatLastSeen(ts?: number) {
    if (!ts) return "Last seen unavailable";
    const diff = Math.max(0, Date.now() - ts);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return "Just now";
    const mins = Math.floor(seconds / 60);
    if (mins === 1) return "1 minute ago";
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs === 1) return "1 hour ago";
    if (hrs < 24) return `${hrs} hours ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
}

export function formatRelativeTime(ts?: number) {
    if (!ts) return "";
    const now = Date.now();
    const diff = now - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return formatBlackspaceDateTime(ts);
}

export function presenceFromStatus(status: string): Presence {
    const s = status.toLowerCase().trim();
    if (s === "online") return { online: true, lastSeen: Date.now() };
    const m = s.match(/last\s*seen\s*(\d+)\s*([hm])\s*ago/);
    if (m) {
        const val = parseInt(m[1], 10);
        const unit = m[2];
        const ms = unit === "h" ? val * 60 * 60 * 1000 : val * 60 * 1000;
        return { online: false, lastSeen: Date.now() - ms };
    }
    return { online: false, lastSeen: undefined };
}

export function getIconOrder(chatId: string | number): FeatureKey[] {
    try {
        const raw = localStorage.getItem(`iconOrder_${chatId}`);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export function addToIconOrder(chatId: string | number, key: FeatureKey) {
    const order = getIconOrder(chatId);
    if (!order.includes(key)) {
        order.push(key);
        localStorage.setItem(`iconOrder_${chatId}`, JSON.stringify(order));
    }
}

export function removeFromIconOrder(chatId: string | number, key: FeatureKey) {
    const order = getIconOrder(chatId).filter((item) => item !== key);
    localStorage.setItem(`iconOrder_${chatId}`, JSON.stringify(order));
}

export function savePreBlockState(chatId: string | number, hadIncognito: boolean) {
    const order = getIconOrder(chatId);
    localStorage.setItem(`preBlock_${chatId}`, JSON.stringify({ order, hadIncognito }));
}

export function restorePreBlockState(chatId: string | number): { order: FeatureKey[]; hadIncognito: boolean } | null {
    try {
        const raw = localStorage.getItem(`preBlock_${chatId}`);
        if (!raw) return null;
        localStorage.removeItem(`preBlock_${chatId}`);
        return JSON.parse(raw);
    } catch {
        return null;
    }
}
